/**
 * a11y-enhancements.js
 *
 * Progressive accessibility repairs applied to the rendered DOM.
 *
 * Everything in here is deliberately non-visual: it only adds semantics
 * (labels, roles, states) or screen-reader-only controls that stay hidden
 * until they receive keyboard focus. No layout, styling or existing
 * behaviour is modified.
 */
(function () {
  'use strict';

  var PAUSE_LABEL = 'Pause background video';
  var PLAY_LABEL = 'Play background video';

  function text(el) {
    return el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function hasAccessibleName(el) {
    if (el.getAttribute('aria-label') && el.getAttribute('aria-label').trim()) return true;
    if (el.getAttribute('aria-labelledby')) return true;
    if (text(el)) return true;
    var img = el.querySelector('img[alt]');
    if (img && img.getAttribute('alt').trim()) return true;
    var svgTitle = el.querySelector('svg title');
    if (svgTitle && text(svgTitle)) return true;
    return false;
  }

  /* ------------------------------------------------------------------
   * 1. Exactly one top level heading (WCAG 1.3.1 / 2.4.6)
   *
   * A few templates have their banner section disabled, which leaves the
   * page without an <h1>. Add a screen-reader-only one built from the
   * page title so the document still exposes a top level heading.
   * ---------------------------------------------------------------- */
  function ensureTopLevelHeading() {
    if (document.querySelector('h1')) return;

    var main = document.getElementById('main-content') || document.querySelector('[role="main"], main');
    if (!main) return;

    var label = (document.title || '').split(/\s+[–—|]\s+/)[0].trim();
    if (!label) {
      var firstHeading = main.querySelector('h2, h3, h4');
      label = text(firstHeading);
    }
    if (!label) return;

    var heading = document.createElement('h1');
    heading.className = 'visually-hidden';
    heading.textContent = label;
    main.insertBefore(heading, main.firstChild);
  }

  /* ------------------------------------------------------------------
   * 2. Auto-playing video needs a pause control (WCAG 2.2.2)
   *
   * Background / hero videos autoplay without controls by design. Add a
   * control that is off screen until it is focused, exactly like the
   * existing "skip to content" shortcut.
   * ---------------------------------------------------------------- */
  function addMediaPauseControls() {
    var videos = document.querySelectorAll('video[autoplay]:not([data-a11y-pause])');

    Array.prototype.forEach.call(videos, function (video) {
      video.setAttribute('data-a11y-pause', 'true');
      if (video.hasAttribute('controls')) return;

      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'screen-reader-shortcut a11y-media-toggle';
      toggle.textContent = PAUSE_LABEL;

      toggle.addEventListener('click', function () {
        if (video.paused) {
          var played = video.play();
          if (played && typeof played.catch === 'function') played.catch(function () {});
          toggle.textContent = PAUSE_LABEL;
        } else {
          video.pause();
          toggle.textContent = PLAY_LABEL;
        }
      });

      if (video.parentNode) {
        video.parentNode.insertBefore(toggle, video.nextSibling);
      }
    });
  }

  /* ------------------------------------------------------------------
   * 3. Media elements need an accessible name (WCAG 1.1.1)
   * ---------------------------------------------------------------- */
  function nameMediaElements() {
    var videos = document.querySelectorAll('video:not([aria-label]):not([aria-labelledby]):not([title])');

    Array.prototype.forEach.call(videos, function (video) {
      var section = video.closest('section, .shopify-section, .row, figure') || document.body;
      var heading = section.querySelector('h1, h2, h3, h4, h5, h6');
      video.setAttribute('aria-label', text(heading) || 'Video');
    });

    var frames = document.querySelectorAll('iframe:not([title])');
    Array.prototype.forEach.call(frames, function (frame) {
      frame.setAttribute('title', 'Embedded content');
    });
  }

  /* ------------------------------------------------------------------
   * 4. `title` must not be the only tooltip (WCAG 1.1.1 / 4.1.2)
   * ---------------------------------------------------------------- */
  function promoteTitleAttributes() {
    var nodes = document.querySelectorAll(
      '[title]:not(link):not(iframe):not(input):not(select):not(textarea):not([aria-label]):not([aria-labelledby])'
    );

    Array.prototype.forEach.call(nodes, function (node) {
      var title = (node.getAttribute('title') || '').trim();
      if (!title) return;
      if (node.getAttribute('aria-hidden') === 'true') return;
      if (hasAccessibleName(node)) return;
      node.setAttribute('aria-label', title);
    });
  }

  /* ------------------------------------------------------------------
   * 5. Empty headings must not be announced (WCAG 1.3.1)
   *
   * They are left in the layout — some carry spacing — but removed from
   * the accessibility tree.
   * ---------------------------------------------------------------- */
  function silenceEmptyHeadings() {
    var headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');

    Array.prototype.forEach.call(headings, function (heading) {
      var named = hasAccessibleName(heading);

      if (heading.getAttribute('data-a11y-empty') === 'true') {
        // Filled in after the first pass (lazy loaded content) — restore it.
        if (named) {
          heading.removeAttribute('role');
          heading.removeAttribute('data-a11y-empty');
        }
        return;
      }

      if (heading.hasAttribute('role') || named) return;
      heading.setAttribute('role', 'presentation');
      heading.setAttribute('data-a11y-empty', 'true');
    });
  }

  /* ------------------------------------------------------------------
   * 6. Dialogs need a name and a focus target (WCAG 4.1.2)
   * ---------------------------------------------------------------- */
  function nameDialogs() {
    var dialogs = document.querySelectorAll('[role="dialog"], [role="alertdialog"], dialog');

    Array.prototype.forEach.call(dialogs, function (dialog) {
      if (!dialog.getAttribute('aria-label') && !dialog.getAttribute('aria-labelledby')) {
        var heading = dialog.querySelector('h1, h2, h3, h4, h5, h6, [role="heading"]');
        dialog.setAttribute('aria-label', text(heading) || 'Dialog');
      }
      if (!dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }
    });
  }

  /* ------------------------------------------------------------------
   * 7. Expandable triggers must expose their state (WCAG 4.1.2)
   * ---------------------------------------------------------------- */
  function markExpandableTriggers() {
    var triggers = document.querySelectorAll('[aria-haspopup]:not([aria-expanded])');

    Array.prototype.forEach.call(triggers, function (trigger) {
      var value = trigger.getAttribute('aria-haspopup');
      // aria-expanded is only meaningful for menu/listbox style popups.
      if (value !== 'true' && value !== 'menu' && value !== 'listbox' && value !== 'tree' && value !== 'grid') return;
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  function run() {
    var tasks = [
      ensureTopLevelHeading,
      addMediaPauseControls,
      nameMediaElements,
      promoteTitleAttributes,
      silenceEmptyHeadings,
      nameDialogs,
      markExpandableTriggers
    ];

    tasks.forEach(function (task) {
      try {
        task();
      } catch (e) {
        /* never let an enhancement break the page */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Apps inject a lot of markup after load — re-run once things settle.
  window.addEventListener('load', function () {
    setTimeout(run, 1200);
  });
})();
